<template>
  <mcw-dialog
    class="qr-payment-dialog"
    ref="dialogRef"
    v-model="modalOpen"
    scrollable
  >
    <mcw-dialog-title>
      <span class="dialog-title-text">
        <mcw-material-icon icon="qr_code_2" class="title-icon" />
        QR на оплату
      </span>
      <mcw-icon-button class="btn_close" @click="close">
        <mcw-material-icon icon="close" />
      </mcw-icon-button>
    </mcw-dialog-title>

    <mcw-dialog-content class="dialog-content">

      <!-- Лоадер при инициализации -->
      <div v-if="loading" class="state-loading">
        <mcw-circular-progress indeterminate />
        <p class="loading-label">Загружаем данные...</p>
      </div>

      <template v-else>

        <!-- ════════════════════════════════════════
             БЛОК: Активный QR-код
        ════════════════════════════════════════ -->
        <transition name="fade-slide">
          <div v-if="qrLink" class="qr-display-block">
            <div class="qr-image-wrap">
              <img
                :src="qrLink"
                class="qr-image"
                alt="QR-код для оплаты"
              />
            </div>
            <p class="qr-hint">
              <mcw-material-icon icon="smartphone" class="hint-icon" />
              Отсканируйте камерой телефона или приложением банка
            </p>
            <mcw-button outlined @click="qrLink = null" class="btn-hide-qr">
              <mcw-material-icon icon="visibility_off" />
              Скрыть QR
            </mcw-button>
          </div>
        </transition>

        <!-- ════════════════════════════════════════
             ВКЛАДКИ: Услуги / Заявки
        ════════════════════════════════════════ -->
        <div class="tabs-wrap">
          <mcw-tab-bar v-model="activeTab">
            <mcw-tab class="tab-title">
              <span>Услуги</span>
              <span v-if="servicesBill.length" class="tab-badge">{{ servicesBill.length }}</span>
            </mcw-tab>
            <mcw-tab class="tab-title">
              <span>Заявки</span>
              <span v-if="paySets.length" class="tab-badge tab-badge--warn">{{ paySets.length }}</span>
            </mcw-tab>
          </mcw-tab-bar>
        </div>

        <!-- ── Таб 0: Список услуг ── -->
        <div v-show="activeTab === 0" class="tab-panel">

          <div v-if="!servicesBill.length" class="empty-state">
            <mcw-material-icon icon="check_circle" class="empty-icon" />
            <p>Нет неоплаченных услуг</p>
          </div>

          <template v-else>
            <!-- Шапка с суммой и кнопкой «Выбрать все» -->
            <div class="services-toolbar">
              <div class="total-chip" :class="{ 'total-chip--ready': selectedTotal.length }">
                <mcw-material-icon icon="payments" />
                <span>Итого: <strong>{{ billSum || '0.00' }}&nbsp;₽</strong></span>
              </div>
              <mcw-button outlined dense @click="selectAll" class="btn-select-all">
                {{ allSelected ? 'Убрать все' : 'Выбрать все' }}
              </mcw-button>
            </div>

            <!-- Список услуг -->
            <ul class="services-list">
              <li
                v-for="service in servicesBill"
                :key="service.id_bill"
                class="service-item"
                :class="{
                  'service-item--selected': service.checked,
                  'service-item--paid': service.status_n === 2,
                  'service-item--inwork': service.status_n === 1,
                  'service-item--locked': service.status_n > 0,
                }"
                @click.self="service.status_n ? null : toggleService(service)"
              >
                <mcw-checkbox
                  v-model="service.checked"
                  :disabled="service.status_n > 0"
                  @change="updateSelectedServices"
                />
                <span class="service-name" @click="service.status_n ? null : toggleService(service)">
                  {{ service.short_name ? service.short_name.replace(/,(?!\s)/g, ', ') : '' }}
                </span>
                <span
                  v-if="service.status_n === 2"
                  class="service-status-badge service-status-badge--paid"
                >
                  <mcw-material-icon icon="check_circle" />
                  Оплачено
                </span>
                <span
                  v-else-if="service.status_n === 1"
                  class="service-status-badge service-status-badge--inwork"
                >
                  <mcw-material-icon icon="hourglass_top" />
                  В работе
                </span>
                <span class="service-price" @click="service.status_n ? null : toggleService(service)">{{ service.price }}&nbsp;₽</span>
              </li>
            </ul>

            <!-- Футер: кнопка заказа QR -->
            <div class="services-footer">
              <mcw-button
                v-if="selectedTotal.length"
                raised
                @click="orderQR"
                :disabled="orderingQR"
                class="btn-order-qr"
              >
                <mcw-circular-progress
                  v-if="orderingQR"
                  medium
                  class='btn-spinner'
                />
                <mcw-material-icon v-else icon="qr_code" style="color:#fff; margin-right:4px;" />
                Сформировать QR
              </mcw-button>
              <p v-else class="hint-select">
                Выберите услуги для формирования QR-кода
              </p>
            </div>
          </template>

        </div>

        <!-- ── Таб 1: Заявки на оплату ── -->
        <div v-show="activeTab === 1" class="tab-panel">

          <div v-if="!paySets.length" class="empty-state">
            <mcw-material-icon icon="inbox" class="empty-icon" />
            <p>Заявок нет</p>
          </div>

          <ul v-else class="pay-sets-list">
            <li
              v-for="payment in paySets"
              :key="payment.id_pay_set"
              class="pay-set-item"
            >
              <!-- Статусная полоска слева -->
              <span
                class="pay-set-status-bar"
                :class="statusBarClass(payment.pay_status_text)"
              />

              <div class="pay-set-body">
                <div class="pay-set-meta">
                  <span class="pay-set-fio">{{ payment.fio }}</span>
                  <div class="pay-set-chips">
                    <span class="chip chip--date">
                      <mcw-material-icon icon="calendar_today" />
                      {{ payment.date_add }}
                    </span>
                    <span class="chip chip--sum">
                      <mcw-material-icon icon="payments" />
                      {{ payment.sum_pay }}&nbsp;₽
                    </span>
                    <span
                      class="chip"
                      :class="statusChipClass(payment.pay_status_text)"
                    >
                      <mcw-material-icon icon="info" />
                      {{ payment.pay_status_text }}
                    </span>
                  </div>
                </div>

                <!-- Действия -->
                <div class="pay-set-actions">
                  <mcw-button outlined dense @click="showQR(payment.hash)" class="btn-show-qr">
                    <mcw-material-icon icon="qr_code" />
                    Открыть QR
                  </mcw-button>
                  <mcw-button outlined dense @click="refreshStatus()" class="btn-refresh">
                    <mcw-material-icon icon="sync" />
                    Статус
                  </mcw-button>
                  <mcw-button
                    v-if="userRank >= 100 && payment.pay_status_text.toLowerCase().includes('оплач')"
                    outlined dense @click="handleRefund(payment.id_pay_set)" class="btn-refund">
                    <mcw-material-icon icon="undo" />
                    Возврат
                  </mcw-button>
                  <mcw-button outlined dense @click="handleDelete(payment.id_pay_set)" class="btn-delete">
                    <mcw-material-icon icon="delete_outline" />
                    Удалить
                  </mcw-button>
                </div>
              </div>
            </li>
          </ul>

        </div>

      </template>
    </mcw-dialog-content>
  </mcw-dialog>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';

export default {
  name: 'QrPaymentDialog',
  emits: ['close'],
  props: {
    id_patient: Number,
  },
  data() {
    return {
      modalOpen: false,
      loading: false,
      orderingQR: false,
      activeTab: 0,
      qrLink: null,
      selectedTotal: [],
    };
  },

  computed: {
    ...mapState({
      _servicesBill: (state) => state.payment.servicesBill,
      paySets: (state) => state.payment.paySets,
    }),
    userRank() {
      return this.$store.getters['user/getUserRank'];
    },
    servicesBill: {
      get() { return this._servicesBill; },
      set(val) { this.UPDATE_SERRVICES_BILL(val); },
    },

    selectableServices() {
      return this.servicesBill.filter((s) => !s.status_n);
    },

    allSelected() {
      return this.selectableServices.length > 0 && this.selectableServices.every((s) => s.checked);
    },

    billSum() {
      if (!this.selectedTotal.length) return null;
      const total = this.selectedTotal.reduce((sum, item) => {
        const price = parseFloat(
          String(item.price).replace(/\s+/g, '').replace(',', '.'),
        );
        // eslint-disable-next-line no-restricted-globals
        return sum + (isNaN(price) ? 0 : price);
      }, 0);
      return total.toFixed(2);
    },
  },

  watch: {
    paySets(val) {
      if (!val.length) {
        this.activeTab = 0;
        this.qrLink = null;
      }
    },
  },

  async mounted() {
    this.loading = true;
    try {
      await Promise.all([
        this.getServicesBill(this.id_patient),
        this.getMyPySets(this.id_patient),
      ]);
      // Если есть активные заявки — сразу показываем их
      if (this.paySets.length) {
        this.activeTab = 1;
      }
    } finally {
      this.loading = false;
    }
  },

  methods: {
    close() {
      this.modalOpen = false;
      this.emitter.emit('closeQrPaymentDialog');
    },
    open() {
      this.modalOpen = true;
    },
    toggleService(service) {
      service.checked = !service.checked;
      this.updateSelectedServices();
    },

    updateSelectedServices() {
      this.selectedTotal = this.servicesBill.filter((s) => s.checked);
    },

    selectAll() {
      const newChecked = !this.allSelected;
      this.servicesBill = this.servicesBill.map((s) => ({
        ...s,
        checked: s.status_n ? s.checked : newChecked,
      }));
      this.updateSelectedServices();
    },

    async orderQR() {
      this.orderingQR = true;
      try {
        const params = {
          id_bill_list: this.selectedTotal.map((s) => s.id_bill).join(', '),
          sum_pay: this.billSum,
          id_patient: this.id_patient,
        };
        await this.getNewPaySet(params);
        if (this.paySets.length) {
          this.activeTab = 1;
        }
      } finally {
        this.orderingQR = false;
      }
    },

    showQR(path) {
      this.qrLink = null;
      this.$nextTick(() => {
        this.qrLink = `https://host2.medsafe.tech/${path}`;
      });
    },

    async refreshStatus() {
      await this.getMyPySets(this.id_patient);
    },

    async handleDelete(id) {
      await this.delPaySet({ id_pay_set: id, id_patient: this.id_patient });
    },

    async handleRefund(id) {
      await this.refound({ id_pay_set: id, id_patient: this.id_patient });
    },

    // Цвет полоски статуса
    statusBarClass(status) {
      if (!status) return 'bar--neutral';
      const s = status.toLowerCase();
      if (s.includes('оплач') || s.includes('успеш')) return 'bar--success';
      if (s.includes('ожида') || s.includes('pending')) return 'bar--warn';
      if (s.includes('отмен') || s.includes('ошибк')) return 'bar--danger';
      return 'bar--neutral';
    },

    statusChipClass(status) {
      if (!status) return 'chip--neutral';
      const s = status.toLowerCase();
      if (s.includes('оплач') || s.includes('успеш')) return 'chip--success';
      if (s.includes('ожида') || s.includes('pending')) return 'chip--warn';
      if (s.includes('отмен') || s.includes('ошибк')) return 'chip--danger';
      return 'chip--neutral';
    },

    ...mapActions('payment', [
      'getServicesBill',
      'getMyPySets',
      'getNewPaySet',
      'delPaySet',
      'refound',
    ]),
    ...mapMutations('payment', ['UPDATE_SERRVICES_BILL']),
  },
};
</script>

<style>
/* ── Базовый контейнер ── */
.qr-payment-dialog {
  --color-primary: #1976d2;
  --color-success: #2e7d32;
  --color-warn: #e65100;
  --color-danger: #c62828;
  --color-neutral: #607d8b;
  --color-surface: #f8f9fa;
  --radius: 12px;
  font-family: inherit;
}

/* ── Заголовок ── */
.qr-payment-dialog .dialog-title-text {
  display: flex;
  align-items: center;
  gap: 8px;
}
.title-icon {
  color: var(--color-primary);
}
.btn_close {
  position: absolute;
  right: 8px;
  top: 8px;
}
.tab-title {
  display: flex;
  justify-content: space-between;
}
/* ── Контент ── */
.qr-payment-dialog .dialog-content {
  min-width: 480px;
  max-width: 580px;
  padding: 0 16px 16px;
}

/* ── Лоадер ── */
.qr-payment-dialog .state-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
  gap: 16px;
}
.qr-payment-dialog .loading-label {
  color: #888;
  margin: 0;
  font-size: 14px;
}

/* ── QR-блок ── */
.qr-payment-dialog .qr-display-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: var(--color-surface);
  border-radius: var(--radius);
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid #e0e0e0;
}
.qr-payment-dialog .qr-image-wrap {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
}
.qr-payment-dialog .qr-image {
  display: block;
  width: 240px;
  height: 240px;
  object-fit: contain;
}
.qr-payment-dialog .qr-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #555;
  margin: 0;
  text-align: center;
}
.qr-payment-dialog .hint-icon {
  font-size: 18px;
  color: var(--color-primary);
}
.btn-hide-qr {
  --mdc-theme-primary: var(--color-neutral);
}

/* ── Вкладки ── */
.qr-payment-dialog .tabs-wrap {
  margin-bottom: 4px;
}
.qr-payment-dialog .tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary);
  color: #fff;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 6px;
  line-height: 1;
}
.qr-payment-dialog .tab-badge--warn {
  background: var(--color-warn);
}

/* ── Панель таба ── */
.qr-payment-dialog .tab-panel {
  padding-top: 12px;
}

/* ── Пустое состояние ── */
.qr-payment-dialog .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 0;
  color: #aaa;
  gap: 8px;
}
.qr-payment-dialog .empty-icon {
  font-size: 48px;
  opacity: 0.4;
}

/* ── Тулбар услуг ── */
.qr-payment-dialog .services-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.qr-payment-dialog .total-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e3f2fd;
  color: var(--color-primary);
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s;
}
.qr-payment-dialog .total-chip--ready {
  background: #c8e6c9;
  color: var(--color-success);
}
.qr-payment-dialog .btn-select-all {
  --mdc-theme-primary: var(--color-primary);
  font-size: 12px;
}

/* ── Список услуг ── */
.qr-payment-dialog .services-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 320px;
  overflow-y: auto;
  border-radius: var(--radius);
  border: 1px solid #e0e0e0;
}
.qr-payment-dialog .service-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s;
  background: #fff;
}
.qr-payment-dialog .service-item:hover {
  background: #f5f5f5;
}
.qr-payment-dialog .service-item--selected {
  background: #e8f5e9;
}
.qr-payment-dialog .service-item--selected:hover {
  background: #dcedc8;
}
.qr-payment-dialog .service-name {
  flex: 1;
  font-size: 14px;
  color: #333;
  min-width: 0;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.35;
  padding-top: 5px;
}
.qr-payment-dialog .service-price {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-primary);
  white-space: nowrap;
  padding-top: 2px;
  flex-shrink: 0;
}

.qr-payment-dialog .service-item--locked {
  cursor: default;
  opacity: 0.8;
}
.qr-payment-dialog .service-item--paid {
  background: #f1f8f1;
}
.qr-payment-dialog .service-item--paid:hover {
  background: #f1f8f1;
}
.qr-payment-dialog .service-item--inwork {
  background: #fff8f0;
}
.qr-payment-dialog .service-item--inwork:hover {
  background: #fff8f0;
}
.qr-payment-dialog .service-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}
.qr-payment-dialog .service-status-badge .mdc-icon {
  font-size: 14px;
}
.qr-payment-dialog .service-status-badge--paid {
  background: #e8f5e9;
  color: var(--color-success);
}
.qr-payment-dialog .service-status-badge--inwork {
  background: #fff3e0;
  color: var(--color-warn);
}

.qr-payment-dialog .services-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 14px;
  min-height: 40px;
}
.qr-payment-dialog .hint-select {
  margin: 0;
  font-size: 13px;
  color: #aaa;
  text-align: right;
}
.qr-payment-dialog .btn-order-qr {
  --mdc-theme-primary: var(--color-primary);
  display: flex;
  align-items: center;
  gap: 6px;
}
.qr-payment-dialog .btn-spinner {
  --mdc-circular-progress-color: #fff;
}

/* ── Список заявок ── */
.qr-payment-dialog .pay-sets-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 380px;
  overflow-y: auto;
}
.qr-payment-dialog .pay-set-item {
  display: flex;
  border: 1px solid #e0e0e0;
  border-radius: var(--radius);
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.qr-payment-dialog .pay-set-status-bar {
  width: 5px;
  flex-shrink: 0;
  background: var(--color-neutral);
}
.bar--success { background: var(--color-success); }
.bar--warn    { background: var(--color-warn); }
.bar--danger  { background: var(--color-danger); }
.bar--neutral { background: var(--color-neutral); }

.qr-payment-dialog .pay-set-body {
  flex: 1;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.qr-payment-dialog .pay-set-fio {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}
.qr-payment-dialog .pay-set-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.qr-payment-dialog .pay-set-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* Чипы */
.qr-payment-dialog .chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 12px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 500;
  background: #eceff1;
  color: #546e7a;
}
.qr-payment-dialog .chip .mdc-icon {
  font-size: 14px;
}
.qr-payment-dialog .chip--date    { background: #e3f2fd; color: #1565c0; }
.qr-payment-dialog .chip--sum     { background: #e8f5e9; color: var(--color-success); }
.qr-payment-dialog .chip--success { background: #e8f5e9; color: var(--color-success); }
.qr-payment-dialog .chip--warn    { background: #fff3e0; color: var(--color-warn); }
.qr-payment-dialog .chip--danger  { background: #ffebee; color: var(--color-danger); }
.qr-payment-dialog .chip--neutral { background: #eceff1; color: var(--color-neutral); }

/* Кнопки действий заявки */
.qr-payment-dialog .pay-set-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.qr-payment-dialog .btn-show-qr  { --mdc-theme-primary: var(--color-primary); }
.qr-payment-dialog .btn-refresh  { --mdc-theme-primary: var(--color-neutral); }
.qr-payment-dialog .btn-refund   { --mdc-theme-primary: var(--color-warn); }
.qr-payment-dialog .btn-delete   { --mdc-theme-primary: var(--color-danger); }

/* ── Анимации ── */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
